import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { setMasterSessionFlag } from "../lib/api";

/**
 * AuthCallbackPage — handles the Emergent auth redirect landing.
 * Emergent sends the user back to /auth-callback#session_id=<sid>.
 * We read the fragment, POST it to /api/auth/session, cookie is set,
 * then navigate to /sage.
 */
export default function AuthCallbackPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      setErr("No session_id in the URL. Try logging in again.");
      return;
    }
    const session_id = decodeURIComponent(m[1]);
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/session`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ session_id }),
          }
        );
        if (!r.ok) {
          const t = await r.text();
          setErr(`Login failed: ${t || r.status}`);
          return;
        }
        // Mirror the server-verified master state into a local flag so the
        // client-side RequireMaster gate stops looping. The auth cookie is
        // http-only; the SPA needs this local mirror to know it's signed in.
        try {
          const data = await r.json();
          setMasterSessionFlag(!!data?.is_master);
        } catch {
          setMasterSessionFlag(false);
        }
        // Strip the fragment from the URL so the session_id never lingers
        window.history.replaceState({}, "", "/auth-callback");
        navigate("/sage", { replace: true });
      } catch (e) {
        setErr(`Login error: ${e.message}`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-sage-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md border border-sage-border p-6 text-center space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-sage-faint">Sage · authentication</div>
        <div className="text-sm text-sage-text">
          {err ? err : "Completing sign-in…"}
        </div>
        {err && (
          <a
            href="/founder-login"
            data-testid="auth-callback-retry"
            className="inline-block border border-sage-accent text-sage-accent px-3 py-2 text-xs uppercase tracking-[0.1em] mt-2 hover:bg-sage-accent hover:text-sage-bg transition-colors"
          >
            Try again
          </a>
        )}
      </div>
    </div>
  );
}
